import { yupResolver } from '@hookform/resolvers/yup';
import { InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import { CountryCode } from 'libphonenumber-js';
import React, { FC, useMemo } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { MAX_LENGTH } from 'src/constants/common';
import { usePhone } from 'src/hooks/usePhone';
import * as yup from 'yup';

import { DANGER_TYPE_LIST, PACKAGING_GROUP_OPTIONS } from '../quoteDetail.const';

import { IDangerousGoods, IDangerousGoodsForm } from './quoteDetailModal.interface';

const DangerousGoodsModal: FC<IDangerousGoods> = ({ handleClose, handleDangerousGoods, defaultFormValues }) => {
    const { t } = useTranslation();
    const { isPhoneValid, isGetCountryCallingCode } = usePhone();

    const defaultValues = useMemo(
        () => ({
            dangerGoodsType: defaultFormValues?.dangerGoodsType || DANGER_TYPE_LIST[0].value,
            unitedNationsNumber: defaultFormValues?.unitedNationsNumber || '',
            packagingGroup: PACKAGING_GROUP_OPTIONS[0].value,
            goodsClass: defaultFormValues?.goodsClass || '',
            description: defaultFormValues?.description || '',
            emergencyContactName: defaultFormValues?.emergencyContactName || '',
            emergencyContactNumber: defaultFormValues?.emergencyContactNumber || '',
            emergencyContactCountryCode: defaultFormValues?.emergencyContactCountryCode || 'CA',
            emergencyContactExt: defaultFormValues?.emergencyContactExt || '',
            emergencyEapnbr: defaultFormValues?.emergencyEapnbr || '1-888-CAN-UTEC',
        }),
        [defaultFormValues]
    );

    const schema = yup.object().shape({
        dangerGoodsType: yup.string().required('Danger goods type is required'),
        unitedNationsNumber: yup.string().required('United nations number is required'),
        packagingGroup: yup.string().required('Packaging group is required'),
        goodsClass: yup.string().required('Goods class is required'),
        description: yup.string().required('Description is required'),
        emergencyContactName: yup.string().required('Emergency contact name is required'),
        emergencyContactNumber: yup
            .string()
            .required('Emergency contact number is required')
            .test('isValidPhone', function (value) {
                const { emergencyContactCountryCode } = this.parent;

                if (value?.length === isGetCountryCallingCode(emergencyContactCountryCode)?.length) {
                    return false;
                }

                const isValid = isPhoneValid(value, emergencyContactCountryCode);

                return isValid;
            }),
        emergencyContactExt: yup.string(),
        emergencyEapnbr: yup.string().required('Emergency EAPNBR is required'),
    });

    const methods = useForm<IDangerousGoodsForm>({
        resolver: yupResolver(schema) as unknown as Resolver<IDangerousGoodsForm>,
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        setValue,
        formState: { isDirty },
    } = methods;

    const onSubmit = (data: IDangerousGoodsForm) => {
        handleDangerousGoods(data);
    };

    const handleOption = (onChange: any) => (e: any) => {
        onChange(e?.value);
    };

    const handleDangerPhoneChange = (country: CountryCode, phone: string, code: string) => {
        setValue('emergencyContactNumber', `+${code}${phone}`);
        setValue('emergencyContactCountryCode', country);
    };

    const handleEmergencyContactExt = (onChange: any) => (e: any) => {
        const value = e.target.value;

        if (value === '' || (value.length <= 6 && /^\d+$/.test(value))) {
            onChange(value);
        }
    };

    return (
        <CommonModal
            title={t('QuotingHub.Common.Dangerous_Goods')}
            handleClose={handleClose}
            size={'max-w-[688px]  overflow-unset'}
            secondaryBtnText={t('Common.Cancel')}
            primaryBtnText={t('Common.Save_Changes')}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            secondaryBtnOnClick={handleClose}
            primaryBtnDisabled={!isDirty}
        >
            <div className="p-4 flex flex-col md:gap-5 gap-3">
                <p className="text-grayLight600 font-normal text-xs ">{t('QuotingHub.QuotingDashboard.dangerous_goods_warning')}</p>
                <Controller
                    name="dangerGoodsType"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <div className="flex sm:flex-row flex-col flex-wrap xxl:gap-5 xl:gap-4 gap-3 py-[12.5px] ">
                            {DANGER_TYPE_LIST.map((val: any, i) => (
                                <div className="flex gap-2" key={`danger_${i}`}>
                                    <Radio id={`danger_${i}`} onChangeFunc={onChange} inputName={'dangerType'} value={val.value} checked={value === val.value} />
                                    <label htmlFor={`danger_${i}`} className="text-textSecondary text-xs font-medium uppercase cursor-pointer">
                                        {val.name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                />
                <div className="grid sm:grid-cols-3 xxl:gap-5 xl:gap-4 gap-3 ">
                    <Controller
                        name="unitedNationsNumber"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputText
                                inputName="unitedNationsNumber"
                                placeholder="UN0000"
                                className={`form_control ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                required={true}
                                label="UN #"
                                labelClassName="block mb-1.5"
                                value={value}
                                onChangeFunc={onChange}
                            />
                        )}
                    />

                    <Controller
                        name="packagingGroup"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <div className="content">
                                <SelectBox
                                    name="packagingGroup"
                                    label={t('QuotingHub.Common.Packaging_Group')}
                                    labelClassName="form_label block mb-1.5"
                                    id="packagingGroup"
                                    className="form_control"
                                    options={PACKAGING_GROUP_OPTIONS}
                                    onChangeFunc={handleOption(onChange)}
                                    value={PACKAGING_GROUP_OPTIONS.find((val: any) => value === val.value)}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="goodsClass"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputText
                                inputType="number"
                                isAllowDecimal={true}
                                maxLength={1}
                                inputName="goodsClass"
                                placeholder={t('QuotingHub.Common.Enter_class')}
                                className={`form_control ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                label={t('Common.Class')}
                                required={true}
                                labelClassName="block mb-1.5"
                                value={value}
                                shouldFormatNumber={true}
                                onChangeFunc={onChange}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="description"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <div className="w-full">
                            <InputText
                                inputName={t('Common.Description')}
                                placeholder={t('QuotingHub.Common.Enter_technical_name_or_description')}
                                className={`form_control ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                label={t('QuotingHub.Common.Technical_Name_or_Description')}
                                required={true}
                                labelClassName="block mb-1.5"
                                value={value}
                                onChangeFunc={onChange}
                            />
                        </div>
                    )}
                />

                <Controller
                    name="emergencyContactName"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <div className="w-full ">
                            <InputText
                                inputName="emergencyContactName"
                                placeholder={t('QuotingHub.Common.Enter_24_Hr_Emergency_Contact_Name')}
                                className={`form_control ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                label={t('QuotingHub.Common.24_Hr_Emergency_Contact_Name')}
                                required={true}
                                labelClassName="block mb-1.5"
                                value={value}
                                onChangeFunc={onChange}
                            />
                        </div>
                    )}
                />

                <div className="grid lg:grid-cols-2 grid-col-1 xxl:gap-5 xl:gap-4 gap-3">
                    <Controller
                        name="emergencyContactNumber"
                        control={control}
                        render={({ field: { value }, fieldState: { error } }) => (
                            <div className="md:flex flex-col">
                                <PhoneInput
                                    label
                                    required
                                    onNumberChange={handleDangerPhoneChange}
                                    name="phone"
                                    errors={error?.message ? true : null}
                                    value={value}
                                    dropdownDelayClassname="!bottom-full !top-auto !mb-1 !mt-0"
                                />
                            </div>
                        )}
                    />
                    <Controller
                        name="emergencyContactExt"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <InputText
                                inputType="number"
                                maxLength={MAX_LENGTH.EXTENSION}
                                inputName="emergencyContactExt"
                                placeholder={t('QuotingHub.Common.Enter_extension')}
                                className="form_control"
                                label={t('Twilio.Ext')}
                                labelClassName="block mb-1.5"
                                value={value}
                                isAllowZeroValue={true}
                                allowZeroToEnter={true}
                                onChangeFunc={handleEmergencyContactExt(onChange)}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="emergencyEapnbr"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <InputText
                            inputName="emergencyEapnbr"
                            placeholder={t('QuotingHub.Common.Enter_emergency_EAPNBR')}
                            className={`form_control ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                            label={t('QuotingHub.Common.Emergency_EAPNBR')}
                            labelWrapClass="!justify-start gap-x-1.5 mb-1.5"
                            labelIconRight={
                                <TooltipCmp message={t('QuotingHub.QuotingDashboard.Emergency_EAPNBR_tooltip')} parentClassName="max-w-[269px]">
                                    <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                                </TooltipCmp>
                            }
                            maxLength={MAX_LENGTH.EAPNBR}
                            required={true}
                            labelClassName="block mb-0"
                            value={value}
                            onChangeFunc={onChange}
                        />
                    )}
                />
            </div>
        </CommonModal>
    );
};

export default DangerousGoodsModal;
